/**
 * Per-route page metadata — the single source for both the prerendered `<head>`
 * (`scripts/prerender.tsx`, via `headHtml`) and client-side navigation
 * (`src/components/document-meta.tsx`). Keep the routes in step with `App.tsx`.
 */

export const SITE_ORIGIN = 'https://brambleapp.xyz';

/** The 1200×630 social preview card in `public/og.png`. */
export const OG_IMAGE_URL = `${SITE_ORIGIN}/og.png`;
const OG_IMAGE_ALT = 'Bramble — the privacy-first baby tracker';

const SITE_DESCRIPTION =
  'A privacy-first baby tracker. Every on-device tracking feature is free; Bramble Sharing adds co-parent sync and babysitting access for $4.99/month.';

export interface RouteMeta {
  title: string;
  description: string;
  /** Ask crawlers not to index this route (utility and not-found pages). */
  noindex?: boolean;
}

export const ROUTE_META: Record<string, RouteMeta> = {
  '/': {
    title: 'Bramble: Private baby tracking',
    description:
      'Bramble is an ad-free baby tracker with every on-device feature free. Add co-parent sync and babysitting access with Bramble Sharing for $4.99/month.',
  },
  '/about': {
    title: 'About Bramble — why we built a private baby tracker',
    description:
      'Bramble is a privacy-first baby tracker built to keep the everyday log free and the optional co-parent sharing simple. Read why we built it.',
  },
  '/privacy': {
    title: 'Privacy Policy — Bramble',
    description:
      'How Bramble handles your family’s data: no ads, no tracking, no data sales, and a plain explanation of what is stored when you sync.',
  },
  '/terms': {
    title: 'Terms of Service — Bramble',
    description:
      'The terms that govern using Bramble, including the free on-device tracker and the optional Bramble Sharing subscription.',
  },
  '/support': {
    title: 'Support — Bramble',
    description:
      'Contact Bramble support, or find answers to common questions about co-parent sync, account recovery, and your data.',
  },
  '/reset': {
    title: 'Reset password — Bramble',
    description: 'Reset the password for your Bramble account.',
    noindex: true,
  },
};

const NOT_FOUND_META: RouteMeta = {
  title: 'Page not found — Bramble',
  description: 'The page you were looking for is not here.',
  noindex: true,
};

/** Normalise `/about/` to `/about`; leave `/` alone. */
function normalise(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') || '/' : pathname;
}

/** The metadata for a pathname, falling back to the not-found entry. */
export function metaForPath(pathname: string): RouteMeta {
  return ROUTE_META[normalise(pathname)] ?? NOT_FOUND_META;
}

/** The absolute, canonical URL for a pathname on the apex host. */
export function canonicalUrl(pathname: string): string {
  return `${SITE_ORIGIN}${normalise(pathname)}`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * JSON-LD for the site as a whole. Same on every page (it describes the app, not
 * the route), written into the head by the prerender.
 */
export function structuredDataHtml(): string {
  const data = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: 'Bramble',
        url: `${SITE_ORIGIN}/`,
      },
      {
        '@type': 'SoftwareApplication',
        name: 'Bramble',
        applicationCategory: 'HealthApplication',
        operatingSystem: 'iOS',
        url: `${SITE_ORIGIN}/`,
        description: SITE_DESCRIPTION,
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      },
    ],
  };

  return `<script type="application/ld+json">\n${JSON.stringify(data, null, 2)}\n    </script>`;
}

/**
 * The `<head>` tags for a route as an HTML string, written between the
 * `<!-- seo:start -->` / `<!-- seo:end -->` markers in `index.html` by the
 * prerender. The client applies the same values through `DocumentMeta`.
 */
export function headHtml(pathname: string): string {
  const meta = metaForPath(pathname);
  const canonical = canonicalUrl(pathname);

  const tags = [
    `<title>${escapeHtml(meta.title)}</title>`,
    `<meta name="description" content="${escapeHtml(meta.description)}" />`,
  ];

  if (meta.noindex) {
    tags.push('<meta name="robots" content="noindex" />');
  } else {
    tags.push(`<link rel="canonical" href="${canonical}" />`);
  }

  tags.push(
    `<meta property="og:title" content="${escapeHtml(meta.title)}" />`,
    `<meta property="og:description" content="${escapeHtml(meta.description)}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:url" content="${canonical}" />`,
    `<meta property="og:site_name" content="Bramble" />`,
    `<meta property="og:image" content="${OG_IMAGE_URL}" />`,
    '<meta property="og:image:width" content="1200" />',
    '<meta property="og:image:height" content="630" />',
    `<meta property="og:image:alt" content="${escapeHtml(OG_IMAGE_ALT)}" />`,
    '<meta name="twitter:card" content="summary_large_image" />',
    `<meta name="twitter:title" content="${escapeHtml(meta.title)}" />`,
    `<meta name="twitter:description" content="${escapeHtml(meta.description)}" />`,
    `<meta name="twitter:image" content="${OG_IMAGE_URL}" />`,
    `<meta name="twitter:image:alt" content="${escapeHtml(OG_IMAGE_ALT)}" />`,
  );

  tags.push(structuredDataHtml());

  return tags.join('\n    ');
}
